import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Building, GraduationCap, BookOpen, CreditCard, ChevronDown, ChevronRight, Wrench, Droplets, Zap, FolderKanban, Megaphone, Plug, Lock, TrendingUp, Cpu, Sparkles, LifeBuoy, ListChecks } from 'lucide-react';
import { supportUnreadCount } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { tradeColor } from '../utils/tradeColors';
import { TradeUnlockModal } from './TradeUnlockModal';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCampaignVaultOpen, setIsCampaignVaultOpen] = useState(true);
  const [unlockTrade, setUnlockTrade] = useState(null);
  const [ticketCount, setTicketCount] = useState(0);

  useEffect(() => {
    if (user?.role === 'admin') {
      supportUnreadCount().then(({ data }) => setTicketCount(data.count)).catch(() => {});
    }
  }, [user]);
  const [tradesSummary, setTradesSummary] = useState(null);

  const openUnlock = (tradeId) => {
    if (!tradesSummary) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trades/summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      }).then((r) => r.json()).then(setTradesSummary).catch(() => {});
    }
    setUnlockTrade(tradeId);
  };

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path);

  // Get user's entitled trades
  const userTrades = user?.trade_entitlements || [];
  const isAdmin = user?.role === 'admin';
  
  // Show all trades for admin, or only entitled trades for users
  const allTrades = [
    { id: 'HVAC', label: 'HVAC', icon: Wrench, path: '/vault?trade=HVAC' },
    { id: 'Plumbing', label: 'Plumbing', icon: Droplets, path: '/vault?trade=Plumbing' },
    { id: 'Electrical', label: 'Electrical', icon: Zap, path: '/vault?trade=Electrical' }
  ];
  const availableTrades = allTrades.map((t) => ({
    ...t,
    owned: isAdmin || userTrades.length === 0 || userTrades.includes(t.id),
  }));

  return (
    <div className="w-64 border-r border-white/10 flex flex-col" style={{ backgroundColor: '#07090c' }}>
      {/* Logo/Brand */}
      <div className="p-4 border-b border-divider">
        <Link to="/start-here" data-testid="sidebar-logo-link">
          <img src="/gritclicks-logo.png" alt="GritClicks" className="h-16 w-auto mx-auto" data-testid="sidebar-logo" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        
        {/* Start Here: Training & Playbook */}
        <Link
          to="/start-here"
          data-testid="sidebar-start-here-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
            isActive('/start-here')
              ? 'bg-orange text-black font-semibold'
              : 'bg-orange/20 text-orange hover:bg-orange/30 border border-orange/40'
          }`}
        >
          <GraduationCap size={20} />
          <span className="font-medium">Start Here</span>
        </Link>

        {/* My Brand - first setup step after Start Here */}
        <Link
          to="/brand"
          data-testid="sidebar-my-brand-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/brand')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <Building size={20} />
          <span className="font-medium">My Brand</span>
        </Link>

        {/* Playbooks - directly under Start Here */}
        <Link
          to="/playbooks"
          data-testid="sidebar-playbooks-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/playbooks')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <BookOpen size={20} />
          <span className="font-medium">Playbooks</span>
        </Link>

        {/* Campaign Vault - Collapsible */}
        <div>
          <button
            onClick={() => setIsCampaignVaultOpen(!isCampaignVaultOpen)}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive('/vault') || isActive('/campaigns')
                ? 'bg-lime/10 text-lime'
                : 'text-light-gray hover:bg-slate-blue hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <FolderKanban size={20} />
              <span className="font-medium">Campaign Vault</span>
            </div>
            {isCampaignVaultOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>

          {/* Trade Sub-Items */}
          {isCampaignVaultOpen && (
            <div className="ml-4 mt-2 space-y-2">
              {availableTrades.map((trade) => {
                const Icon = trade.icon;
                if (!trade.owned) {
                  return (
                    <button
                      key={trade.id}
                      onClick={() => openUnlock(trade.id)}
                      data-testid={`sidebar-locked-${trade.id}`}
                      className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray hover:bg-slate-blue hover:text-orange transition-all"
                    >
                      <Lock size={16} />
                      <span className="text-sm">{trade.label}</span>
                      <span className="ml-auto text-[10px] font-bold uppercase text-orange">Unlock</span>
                    </button>
                  );
                }
                return (
                  <Link
                    key={trade.id}
                    to={trade.path}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                      location.search.includes(`trade=${trade.id}`)
                        ? `${tradeColor(trade.id).sidebar} font-semibold`
                        : 'text-light-gray hover:bg-slate-blue hover:text-white'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-sm">{trade.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Growth Center */}
        <Link
          to="/growth"
          data-testid="sidebar-growth-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/growth')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <TrendingUp size={20} />
          <span className="font-medium">Growth Center</span>
        </Link>

        {/* AI Coach */}
        <Link
          to="/coach"
          data-testid="sidebar-coach-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/coach')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <Sparkles size={20} />
          <span className="font-medium">AI Coach</span>
        </Link>

        {/* AI ROI Advisor */}
        <Link
          to="/advisor"
          data-testid="sidebar-advisor-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/advisor')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <TrendingUp size={20} />
          <span className="font-medium">AI Advisor</span>
        </Link>

        {/* Marketing Automation */}
        <Link
          to="/automation"
          data-testid="sidebar-automation-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/automation')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <Megaphone size={20} />
          <span className="font-medium">Marketing Automation</span>
        </Link>

        {/* Connections */}
        <Link
          to="/connections"
          data-testid="sidebar-connections-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/connections')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <Plug size={20} />
          <span className="font-medium">Connections</span>
        </Link>

        {/* Booking Integrations */}
        <Link
          to="/integrations"
          data-testid="sidebar-integrations-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/integrations')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <Plug size={20} />
          <span className="font-medium">Integrations</span>
        </Link>

        {/* Account & Billing */}
        <Link
          to="/account"
          data-testid="sidebar-account-link"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive('/account')
              ? 'bg-lime/10 text-lime'
              : 'text-light-gray hover:bg-slate-blue hover:text-white'
          }`}
        >
          <CreditCard size={20} />
          <span className="font-medium">Account</span>
        </Link>

        {user?.role === 'admin' && (
          <Link
            to="/admin/support"
            data-testid="sidebar-support-tickets-link"
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive('/admin/support')
                ? 'bg-lime/10 text-lime'
                : 'text-light-gray hover:bg-slate-blue hover:text-white'
            }`}
          >
            <LifeBuoy size={20} />
            <span className="font-medium">Support Tickets</span>
            {ticketCount > 0 && (
              <span className="ml-auto bg-orange text-black text-xs font-black px-2 py-0.5 rounded-full" data-testid="support-unread-badge">
                {ticketCount}
              </span>
            )}
          </Link>
        )}

        {user?.role === 'admin' && (
          <Link
            to="/admin/ai-usage"
            data-testid="sidebar-ai-usage-link"
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive('/admin/ai-usage')
                ? 'bg-lime/10 text-lime'
                : 'text-light-gray hover:bg-slate-blue hover:text-white'
            }`}
          >
            <Cpu size={20} />
            <span className="font-medium">AI Usage (Admin)</span>
          </Link>
        )}

        {user?.role === 'admin' && (
          <Link
            to="/admin/campaign-reference"
            data-testid="sidebar-campaign-reference-link"
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive('/admin/campaign-reference')
                ? 'bg-lime/10 text-lime'
                : 'text-light-gray hover:bg-slate-blue hover:text-white'
            }`}
          >
            <ListChecks size={20} />
            <span className="font-medium">Campaign Reference</span>
          </Link>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-divider">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm">
            <div className="text-white font-medium">{user?.name || user?.email}</div>
            {user?.role === 'admin' && (
              <div className="text-xs text-construction-orange">Admin</div>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-light-gray hover:bg-orange/10 hover:text-orange transition-colors"
        >
          <LogOut size={18} />
          <span className="text-sm">Logout</span>
        </button>
      </div>
      {unlockTrade && (
        <TradeUnlockModal trade={unlockTrade} summary={tradesSummary} onClose={() => setUnlockTrade(null)} />
      )}
    </div>
  );
};

export default Sidebar;
